import React, { useEffect, useState } from 'react';
import { RefreshCw, Search, User, Eye, Edit, Trash2 } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { Toast } from '../components/Toast';
import { UserDetailPanel, UserEditModal, UserDeleteModal } from '../components/modals/UserModals';
import * as mockApi from '../services/mockApi';
import type { User as UserType } from '../types';
export function UtentiPage() {
  const [users, setUsers] = useState<UserType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Modal states
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [deletingUser, setDeletingUser] = useState<UserType | null>(null);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Load users
  useEffect(() => {
    loadUsers();
  }, []);
  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await mockApi.getUsers();
      setUsers(data);
    } catch (error) {
      setToast({
        message: 'Errore nel caricamento degli utenti',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const syncedUsers = await mockApi.syncUsers();
      setUsers(syncedUsers);
      setToast({
        message: 'Sincronizzazione completata',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nella sincronizzazione',
        type: 'error'
      });
    } finally {
      setIsSyncing(false);
    }
  };
  const handleView = async (userId: string) => {
    try {
      const user = await mockApi.getUser(userId);
      setSelectedUser(user);
    } catch (error) {
      setToast({
        message: 'Errore nel caricamento dei dettagli',
        type: 'error'
      });
    }
  };
  const handleSaveEdit = async (id: string, data: Partial<UserType>) => {
    try {
      const updated = await mockApi.updateUser(id, data);
      setUsers(prev => prev.map(u => u.id === id ? updated : u));
      setToast({
        message: 'Utente aggiornato',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nel salvataggio',
        type: 'error'
      });
      throw error;
    }
  };
  const handleDelete = async (id: string) => {
    try {
      await mockApi.deleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
      setToast({
        message: 'Utente eliminato',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: "Errore nell'eliminazione",
        type: 'error'
      });
      throw error;
    }
  };
  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    return user.nome.toLowerCase().includes(searchLower) || user.cognome.toLowerCase().includes(searchLower) || user.email.toLowerCase().includes(searchLower) || user.note.toLowerCase().includes(searchLower);
  });
  if (isLoading) {
    return <div className="flex items-center justify-center py-20" data-id="element-2104">
        <p className="text-gray-500 dark:text-gray-400" data-id="element-2105">Caricamento...</p>
      </div>;
  }
  return <div className="pb-12 space-y-6" data-id="element-2106">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-2107" />}

      {/* Header with Sync Button */}
      <div className="flex items-center justify-between" data-id="element-2108">
        <div data-id="element-2109">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-2110">
            Utenti
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2111">
            Gestisci gli utenti della piattaforma
          </p>
        </div>
        <AppButton variant="primary" icon={<RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} data-id="element-2113" />} onClick={handleSync} disabled={isSyncing} data-id="element-2112">
          {isSyncing ? 'Sincronizzazione...' : 'Sync con DB'}
        </AppButton>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4" data-id="element-2114">
        <div className="flex-1 relative" data-id="element-2115">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" data-id="element-2116" />
          <input type="text" placeholder="Cerca utenti..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-180 shadow-sm" data-id="element-2117" />
        </div>
      </div>

      {/* Users Table */}
      <AppCard padding="none" className="overflow-hidden" data-id="element-2118">
        <div className="overflow-x-auto" data-id="element-2119">
          <table className="w-full" data-id="element-2120">
            <thead className="bg-gray-50 dark:bg-dark-subtle border-b border-gray-100 dark:border-dark-medium" data-id="element-2121">
              <tr data-id="element-2122">
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2123">
                  Utente
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2124">
                  Nome
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2125">
                  Cognome
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2126">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2127">
                  Note
                </th>
                <th className="px-6 py-4 text-right text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2128">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-medium" data-id="element-2129">
              {filteredUsers.length === 0 ? <tr data-id="element-2130">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 text-sm" data-id="element-2131">
                    Nessun utente trovato
                  </td>
                </tr> : filteredUsers.map(user => <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors duration-150" data-id="element-2132">
                    <td className="px-6 py-4 whitespace-nowrap w-12" data-id="element-2133">
                      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-dark-medium flex items-center justify-center text-gray-500 dark:text-gray-400" data-id="element-2134">
                        <User className="w-4 h-4" data-id="element-2135" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2136">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2137">
                        {user.nome}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2138">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2139">
                        {user.cognome}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2140">
                      <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2141">
                        {user.email}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2142">
                      <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2143">
                        {user.note || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-2144">
                      <div className="flex items-center justify-end gap-1" data-id="element-2145">
                        <button onClick={() => handleView(user.id)} className="group relative p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-180" title="Vedi" data-id="element-2146">
                          <Eye className="w-4 h-4" data-id="element-2147" />
                        </button>
                        <button onClick={() => setEditingUser(user)} className="group relative p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-180" title="Modifica" data-id="element-2148">
                          <Edit className="w-4 h-4" data-id="element-2149" />
                        </button>
                        <button onClick={() => setDeletingUser(user)} className="group relative p-2 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-all duration-180" title="Elimina" data-id="element-2150">
                          <Trash2 className="w-4 h-4" data-id="element-2151" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </AppCard>

      {/* Modals */}
      <UserDetailPanel isOpen={!!selectedUser} user={selectedUser} onClose={() => setSelectedUser(null)} data-id="element-2152" />

      <UserEditModal isOpen={!!editingUser} user={editingUser} onClose={() => setEditingUser(null)} onSave={handleSaveEdit} data-id="element-2153" />

      <UserDeleteModal isOpen={!!deletingUser} user={deletingUser} onClose={() => setDeletingUser(null)} onDelete={handleDelete} data-id="element-2154" />
    </div>;
}